import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * `cn` — the shadcn/ui canonical class-name helper.
 *
 * Two layers:
 *   1. `clsx` resolves conditional / array / object class inputs
 *      into a flat space-separated string ("foo bar baz").
 *   2. `tailwind-merge` deduplicates conflicting Tailwind classes
 *      so a later `bg-red-500` correctly beats an earlier
 *      `bg-blue-500` instead of both ending up in the DOM.
 *
 * This is the single import every shadcn-style component reaches
 * for when composing classes. Keep it identical across the app so
 * future shadcn re-pulls drop in without rewriting imports.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Human-readable byte size. Bumped through B/KB/MB/GB/TB. Drops to
 * two decimal places past GB so "1.23 GB" stays readable in tables.
 * One canonical implementation — was duplicated 5+ times across
 * pages before consolidation.
 */
export function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  if (n < 1024 ** 4) return `${(n / 1024 ** 3).toFixed(2)} GB`;
  return `${(n / 1024 ** 4).toFixed(2)} TB`;
}

/**
 * Relative time string from an ISO timestamp. "just now" / "2m ago"
 * / "3h ago" / "5d ago" / falls to a localized date past 30 days
 * because "47d ago" is harder to mentally pin to a real date than
 * "Apr 4, 2026". Returns "—" for null/undefined so callers can pass
 * DB-null fields directly without a guard.
 */
export function relativeTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '—';
  const diffSec = (Date.now() - then) / 1000;
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  const days = Math.floor(diffSec / 86400);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

/**
 * Inline-style fragment for the checkered "transparent" background
 * applied to image previews + thumbnails. Was duplicated across
 * AssetPreview, ImageOverlayDiff, ThumbnailCard. CSS-only — applies
 * to any element via `style={CHECKER_BG}`.
 */
export const CHECKER_BG: React.CSSProperties = {
  background: 'repeating-conic-gradient(#1a1f29 0% 25%, #0d1117 0% 50%) 50% / 16px 16px',
};
